import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  TablePagination,
  Skeleton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import { getScheduledAlerts, deleteScheduledAlert } from "../../collections/scheduledAlerts";

const priorityColors = {
  low: "default",
  medium: "primary",
  high: "error",
};

const statusColors = {
  pending: "warning",
  triggered: "success",
  cancelled: "default",
};

const priorityLabels = {
  low: "Thấp",
  medium: "Trung bình",
  high: "Cao",
};

const statusLabels = {
  pending: "Chờ xử lý",
  triggered: "Đã gửi",
  cancelled: "Đã hủy",
};

const ScheduledAlertsList = (props) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [forceRerender, setForceRerender] = useState(false);

  useEffect(() => {
    props.setPageTitle("Nhắc nhở");

    const fetchAlerts = async () => {
      setLoading(true);
      try {
        const params = {
          page: page + 1,
          per_page: rowsPerPage,
        };
        if (statusFilter) {
          params.status = statusFilter;
        }
        if (priorityFilter) {
          params.priority = priorityFilter;
        }
        const res = await getScheduledAlerts(params);
        setAlerts(res.data);
        setTotalRows(res.total);
      } catch (error) {
        console.error("Failed to fetch alerts", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
  }, [page, rowsPerPage, statusFilter, priorityFilter, props, forceRerender]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditAlert = (id) => {
    navigate(`/scheduled-alerts/${id}/edit`);
  };

  const handleCancelAlert = async (alertId) => {
    const confirmAction = window.confirm("Bạn có chắc muốn hủy nhắc nhở này không?");

    if (confirmAction) {
      try {
        await deleteScheduledAlert(alertId);
        setForceRerender((prev) => !prev);
      } catch (error) {
        console.error("Failed to cancel alert", error);
      }
    }
  };

  if (loading) {
    return <Skeleton animation="wave" variant="rectangular" height={250} sx={{ mt: 3 }} />;
  }

  return (
    <Container sx={{ mt: 3 }}>
      <Grid container alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Grid item>
          <Button
            variant="outlined"
            onClick={() => navigate("/scheduled-alerts/new")}
            startIcon={<AddIcon />}
          >
            Tạo nhắc nhở
          </Button>
        </Grid>
        <Grid item>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Trạng thái</InputLabel>
            <Select
              value={statusFilter}
              label="Trạng thái"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">Tất cả</MenuItem>
              <MenuItem value="pending">Chờ xử lý</MenuItem>
              <MenuItem value="triggered">Đã gửi</MenuItem>
              <MenuItem value="cancelled">Đã hủy</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Độ ưu tiên</InputLabel>
            <Select
              value={priorityFilter}
              label="Độ ưu tiên"
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">Tất cả</MenuItem>
              <MenuItem value="low">Thấp</MenuItem>
              <MenuItem value="medium">Trung bình</MenuItem>
              <MenuItem value="high">Cao</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Tiêu đề</TableCell>
              <TableCell>Thời gian</TableCell>
              <TableCell>Độ ưu tiên</TableCell>
              <TableCell>Trạng thái</TableCell>
              <TableCell>Khách hàng</TableCell>
              <TableCell>Người tạo</TableCell>
              <TableCell>Hành động</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alerts.map((alert) => (
              <TableRow key={alert.id} hover sx={{ cursor: "pointer" }}>
                <TableCell>{alert.id}</TableCell>
                <TableCell>{alert.title}</TableCell>
                <TableCell>
                  {alert.scheduled_at
                    ? dayjs(alert.scheduled_at).format("DD/MM/YYYY HH:mm")
                    : "-"}
                </TableCell>
                <TableCell>
                  <Chip
                    label={priorityLabels[alert.priority] || alert.priority}
                    color={priorityColors[alert.priority] || "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={statusLabels[alert.status] || alert.status}
                    color={statusColors[alert.status] || "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>{alert.user_name || "-"}</TableCell>
                <TableCell>{alert.creator_name || "-"}</TableCell>
                <TableCell>
                  {alert.status === "pending" && (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleEditAlert(alert.id);
                        }}
                        sx={{ mr: 1 }}
                      >
                        Sửa
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCancelAlert(alert.id);
                        }}
                      >
                        Hủy
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Container>
  );
};

export default ScheduledAlertsList;
